import { useMemo, useState } from 'react'
import type { MapPoint, Coop } from '../lib/types'

interface Props {
  points: MapPoint[]
  personalDebtIds: Set<string>
  coop: Coop | null
  onPointClick: (p: MapPoint) => void
  onRunNuanceMachine?: (clusterHint: { x: number; y: number }) => void
}

export function CognitiveMap({ points, personalDebtIds, coop, onPointClick, onRunNuanceMachine }: Props) {
  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState({ x: 0, y: 0 })
  const [hovered, setHovered] = useState<MapPoint | null>(null)

  const viewW = 640
  const viewH = 520
  const pad = 42

  const filtered = points // parent already filters by coop + evidence

  // Simple density for "evidence contours"
  const contours = useMemo(() => {
    const gx = 11, gy = 11
    const g: number[][] = Array.from({ length: gy }, () => Array(gx).fill(0))
    filtered.forEach(p => {
      const ix = Math.min(gx - 1, Math.max(0, Math.floor(p.x * gx)))
      const iy = Math.min(gy - 1, Math.max(0, Math.floor((1 - p.y) * gy)))
      g[iy][ix] += 0.8 + (p.evidence / 12) * 1.6
    })
    const s = g.map(r => [...r])
    for (let y = 1; y < gy - 1; y++) for (let x = 1; x < gx - 1; x++) {
      s[y][x] = (g[y][x] + g[y-1][x] + g[y+1][x] + g[y][x-1] + g[y][x+1]) / 5
    }
    const levels = [1.4, 2.6, 3.8]
    return levels.map((lev) => {
      const paths: string[] = []
      for (let y = 0; y < gy - 1; y++) {
        for (let x = 0; x < gx - 1; x++) {
          const a = s[y][x] >= lev ? 1 : 0
          const b = s[y][x + 1] >= lev ? 1 : 0
          const c = s[y + 1][x + 1] >= lev ? 1 : 0
          const d = s[y + 1][x] >= lev ? 1 : 0
          const sum = a + b + c + d
          if (sum === 0 || sum === 4) continue
          const cx = pad + ((x + 0.5) / gx) * (viewW - pad * 2)
          const cy = pad + ((y + 0.5) / gy) * (viewH - pad * 2)
          const r = 22 + (lev - 1.4) * 4
          paths.push(`M ${cx - r} ${cy} Q ${cx} ${cy - r * 0.85} ${cx + r} ${cy} Q ${cx} ${cy + r * 0.82} ${cx - r} ${cy}`)
        }
      }
      return { d: paths.join(' ') }
    })
  }, [filtered])

  const toScreen = (p: MapPoint) => ({
    sx: pad + p.x * (viewW - pad * 2),
    sy: pad + (1 - p.y) * (viewH - pad * 2),
  })

  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }) }

  return (
    <div className="select-none">
      <div className="flex items-center justify-between mb-2 px-1">
        <div>
          <div className="section-title">EVIDENCE-WEIGHTED COGNITIVE TOPOGRAPHY</div>
          <div className="text-xl tracking-tight font-semibold mt-0.5">
            {coop ? coop.name : 'Public Cooperative Field'}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button onClick={() => setZoom(z => Math.min(2.8, z + 0.18))} className="btn btn-ghost px-2 py-0.5 text-xs">＋</button>
          <button onClick={() => setZoom(z => Math.max(0.6, z - 0.18))} className="btn btn-ghost px-2 py-0.5 text-xs">－</button>
          <button onClick={resetView} className="btn btn-ghost px-2 py-0.5 text-xs">Reset</button>
          <span className="text-[var(--text-subtle)] tabular-nums">{filtered.length} mapped</span>
        </div>
      </div>

      <svg
        viewBox={`0 0 ${viewW} ${viewH}`}
        width="100%"
        className="map-container"
        onWheel={(e) => {
          e.preventDefault()
          const dir = e.deltaY > 0 ? -0.11 : 0.11
          setZoom(z => Math.max(0.55, Math.min(3.0, z + dir)))
        }}
        onMouseMove={(e) => {
          if (e.buttons !== 1) return
          setPan(p => ({ x: p.x + e.movementX * 0.75, y: p.y + e.movementY * 0.75 }))
        }}
      >
        <defs>
          <pattern id="coopfield" width="26" height="26" patternUnits="userSpaceOnUse">
            <path d="M 26 0 L 0 0 0 26" fill="none" stroke="var(--border)" strokeWidth="0.5" opacity="0.45"/>
          </pattern>
        </defs>

        <rect x="0" y="0" width={viewW} height={viewH} fill="var(--map-bg)" />
        <rect x={pad-8} y={pad-8} width={viewW - pad*2 + 16} height={viewH - pad*2 + 16} fill="url(#coopfield)" />

        {/* Grammar labels */}
        <text x={pad} y={viewH - 9} className="text-[9px]" fill="var(--text-subtle)">← low avoidance (easy to surface)</text>
        <text x={viewW - pad - 6} y={viewH - 9} textAnchor="end" className="text-[9px]" fill="var(--text-subtle)">high unseen courage →</text>
        <text x="13" y={pad + 11} transform={`rotate(-90 13 ${pad + 11})`} className="text-[9px]" fill="var(--text-subtle)">strong repair orientation ↑</text>
        <text x="13" y={viewH - pad - 3} transform={`rotate(-90 13 ${viewH - pad - 3})`} className="text-[9px]" fill="var(--text-subtle)">weak willingness / no closure ↓</text>

        <g transform={`translate(${pan.x} ${pan.y}) scale(${zoom})`}>
          {/* Evidence contours */}
          {contours.map((c, i) => (
            <path key={i} d={c.d} className="contour" style={{ strokeOpacity: 0.18 + i * 0.11, strokeWidth: 2.2 - i * 0.35 }} />
          ))}

          {/* Grid */}
          {[0.25, 0.5, 0.75].map((t, idx) => (
            <g key={idx} opacity="0.28">
              <line x1={pad + t * (viewW - pad * 2)} y1={pad} x2={pad + t * (viewW - pad * 2)} y2={viewH - pad} stroke="var(--border)" strokeWidth="0.6" />
              <line x1={pad} y1={pad + (1 - t) * (viewH - pad * 2)} x2={viewW - pad} y2={pad + (1 - t) * (viewH - pad * 2)} stroke="var(--border)" strokeWidth="0.6" />
            </g>
          ))}

          {/* Points sized + colored by evidence weight */}
          {filtered.map(p => {
            const { sx, sy } = toScreen(p)
            const isPersonal = personalDebtIds.has(p.id)
            const r = 2.6 + Math.min(4.8, (p.evidence - 2) * 0.55)
            return (
              <g key={p.id} onClick={() => onPointClick(p)} onMouseEnter={() => setHovered(p)} onMouseLeave={() => setHovered(null)}>
                {p.evidence > 6.5 && (
                  <circle cx={sx} cy={sy} r={r + 6.5} className="anomaly-ring" />
                )}
                <circle
                  cx={sx} cy={sy} r={r}
                  fill={p.has_closure ? 'var(--accent)' : 'var(--accent-warm)'}
                  stroke={isPersonal ? 'var(--accent-warm)' : 'none'}
                  strokeWidth={isPersonal ? 2.2 : 0}
                  className={isPersonal ? 'point personal-point' : 'point'}
                  opacity={isPersonal ? 0.98 : 0.78 + Math.min(0.2, (p.evidence - 3) / 18)}
                />
                {isPersonal && <circle cx={sx} cy={sy} r={r + 2.2} fill="none" stroke="var(--accent-warm)" strokeWidth="1" opacity="0.4" />}
              </g>
            )
          })}
        </g>

        {hovered && (
          <g>
            <rect x={viewW - 212} y="14" width="198" height="62" rx="8" fill="var(--bg)" stroke="var(--border)" />
            <text x={viewW - 200} y="30" fontSize="10.5" fill="var(--text)">{hovered.debt_item}</text>
            <text x={viewW - 200} y="45" fontSize="9.5" fill="var(--text-subtle)">avoid {hovered.avoidance.toFixed(1)} · repair {hovered.repair.toFixed(1)}</text>
            <text x={viewW - 200} y="58" fontSize="9.5" fill="var(--accent)">evidence weight {hovered.evidence.toFixed(1)}</text>
          </g>
        )}
      </svg>

      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-[var(--text-subtle)] px-1">
        <span>● size = evidence weight (community + closure + repetition)</span>
        <span>ring = high-evidence pattern</span>
        <span>filled = has concrete closure action</span>
        {onRunNuanceMachine && (
          <button onClick={() => onRunNuanceMachine({ x: 0.5, y: 0.5 })} className="btn btn-ghost text-[10px] px-2 py-0.5 ml-auto">Run nuance machine on field</button>
        )}
      </div>
    </div>
  )
}
